import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, MessagesSquare } from "lucide-react";

import PageHeader from "@/components/layout/PageHeader";
import EmptyState from "@/components/common/EmptyState";
import SearchField from "@/components/common/SearchField";
import { Button } from "@/components/ui/button";
import DealChat, { previewTime } from "@/components/deals/chat/DealChat";
import { useAuth } from "@/lib/auth";
import { useChatSocketStatus } from "@/lib/chatSocket";
import { useChatInbox } from "@/hooks/useDealChat";
import { cn } from "@/utils/common.utils";
import type { ChatInboxDeal } from "@/types/chat.types";

// Matches DealChat's own frame height so the two panels line up.
const PANEL_HEIGHT = "lg:h-[min(680px,calc(100dvh-140px))]";

function DealRow({ deal, active, viewerId, onSelect }: {
  deal: ChatInboxDeal;
  active: boolean;
  viewerId?: string;
  onSelect: () => void;
}) {
  const last = deal.last_message;
  const who = last ? (last.sender_id === viewerId ? "You" : (last.sender_name || "").split(" ")[0]) : "";
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          "flex w-full items-start gap-3 border-b border-dash-border px-4 py-3.5 text-left transition-colors",
          active ? "bg-dash-hover" : "hover:bg-dash-searchBg",
        )}
        aria-current={active ? "true" : undefined}
        data-testid={`chats-deal-${deal.deal_id}`}
      >
        <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-dash-hover text-dash-greenDeep" aria-hidden="true">
          <MessagesSquare className="size-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2">
            <span className={cn("truncate text-sm text-dash-ink", deal.unread_count ? "font-semibold" : "font-medium")}>{deal.address}</span>
            <span className="shrink-0 text-[11px] text-dash-faint">{previewTime(last?.created_at)}</span>
          </span>
          <span className="block truncate text-[11px] uppercase tracking-[0.08em] text-dash-faint">
            {deal.is_internal
              ? `${deal.thread_count} conversation${deal.thread_count === 1 ? "" : "s"}`
              : `You’re the ${(deal.role_label || "participant").toLowerCase()}`}
          </span>
          <span className="mt-0.5 flex items-center justify-between gap-2">
            <span className={cn("truncate text-xs", deal.unread_count ? "text-dash-ink" : "text-dash-muted")}>
              {last ? `${who}: ${last.body}` : "No messages yet — say hello"}
            </span>
            {deal.unread_count > 0 && (
              <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-semibold text-white" aria-label={`${deal.unread_count} unread`}>
                {deal.unread_count > 99 ? "99+" : deal.unread_count}
              </span>
            )}
          </span>
        </span>
      </button>
    </li>
  );
}

/** Past this many deals the list gets a search box. */
const SEARCH_FROM = 5;

/**
 * Every deal this person can chat on, with its conversations beside it — one
 * place to answer across all of them without opening each deal. Agents see
 * their brokerage's deals that have conversations (each one opening its list
 * of clients, lenders and co-agents); participants see the rooms they were
 * invited into.
 */
export default function ChatsPage() {
  const { user } = useAuth();
  const socketStatus = useChatSocketStatus();
  const { data, isLoading, isError, refetch } = useChatInbox(socketStatus);
  const [searchParams, setSearchParams] = useSearchParams();
  const deals = useMemo(() => data?.deals ?? [], [data]);
  const [query, setQuery] = useState("");
  const needle = query.trim().toLowerCase();
  const shownDeals = needle ? deals.filter((d) => d.address.toLowerCase().includes(needle)) : deals;
  const requested = searchParams.get("deal");
  const selected = deals.find((d) => d.deal_id === requested) || null;
  // Desktop always shows a conversation: the requested one, else whichever was
  // newest when the page opened. Pinned rather than recomputed, or a message
  // landing on another deal would reorder the list and swap the conversation
  // out from under the person reading it.
  const [defaultId, setDefaultId] = useState<string | null>(null);
  useEffect(() => {
    if (!defaultId && deals[0]) setDefaultId(deals[0].deal_id);
  }, [defaultId, deals]);
  const active = selected || deals.find((d) => d.deal_id === defaultId) || deals[0] || null;

  const select = (dealId: string | null) => {
    setSearchParams((current) => {
      const params = new URLSearchParams(current);
      if (dealId) params.set("deal", dealId);
      else params.delete("deal");
      return params;
    }, { replace: !dealId });
  };

  // A link to a deal the person has since lost access to falls back quietly.
  useEffect(() => {
    if (requested && data && !selected) select(null);
  }, [requested, data, selected]); // eslint-disable-line react-hooks/exhaustive-deps

  let content;
  if (isLoading) {
    content = <div className={cn("h-[440px] animate-pulse rounded-xl bg-dash-searchBg/60", PANEL_HEIGHT)} aria-busy="true" />;
  } else if (isError && !data) {
    content = (
      <EmptyState
        compact
        icon={MessagesSquare}
        title="Chats couldn’t be loaded"
        description="Check your connection and try again."
        action={<Button type="button" variant="dashboardOutline" size="sm" className="mt-4" onClick={() => refetch()}>Try again</Button>}
      />
    );
  } else if (!deals.length) {
    content = (
      <EmptyState
        compact
        icon={MessagesSquare}
        title="No chats yet"
        description="A conversation appears here as soon as someone joins one of your deals — a client, lender, attorney or co-agent accepting their invitation."
        data-testid="chats-empty"
      />
    );
  } else {
    content = (
      <div className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
        {/* On a phone the list and the conversation take turns. */}
        {/* Same shape as the right column — a title row over a panel — so the
            two panels line up. */}
        <div className={cn("min-w-0 flex-col gap-3 lg:flex", selected ? "hidden" : "flex")}>
          <div className="flex h-7 items-center">
            <h2 className="text-base font-semibold text-dash-ink">Your deals</h2>
          </div>
          <section
            className={cn("flex flex-col overflow-hidden rounded-xl border border-dash-border bg-white", PANEL_HEIGHT)}
            data-testid="chats-deal-list"
          >
            {deals.length > SEARCH_FROM && (
              <div className="border-b border-dash-border p-3">
                <SearchField
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onClear={() => setQuery("")}
                  placeholder="Search by address…"
                  inputClassName="h-10"
                  testId="chats-search"
                />
              </div>
            )}
            <ul className="min-h-0 flex-1 overflow-y-auto">
              {!shownDeals.length && (
                <li className="px-4 py-8 text-center text-sm text-dash-muted">No deals match “{query.trim()}”.</li>
              )}
              {shownDeals.map((deal) => (
                <DealRow key={deal.deal_id} deal={deal} active={deal.deal_id === active?.deal_id} viewerId={user?.id} onSelect={() => select(deal.deal_id)} />
              ))}
            </ul>
          </section>
        </div>

        {active && user && (
          <div className={cn("min-w-0 flex-col gap-3 lg:flex", selected ? "flex" : "hidden")}>
            <div className="flex h-7 items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-1">
                <Button type="button" variant="seamless" onClick={() => select(null)} className="-ml-1 p-1 text-dash-muted hover:text-dash-ink lg:hidden" aria-label="Back to your deals">
                  <ChevronLeft className="size-5" />
                </Button>
                <h2 className="truncate text-base font-semibold text-dash-ink">{active.address}</h2>
              </div>
              <Link to={active.route} className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-dash-greenDeep hover:underline" data-testid="chats-open-deal">
                Open deal <ArrowUpRight className="size-3.5" />
              </Link>
            </div>
            <DealChat
              key={active.deal_id}
              dealId={active.deal_id}
              viewer={{
                id: user.id,
                name: user.name,
                role: active.participant_role || "agent",
                internal: active.is_internal,
              }}
              socketStatus={socketStatus}
              splitAt="2xl"
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="font-dashboard" data-testid="chats-page">
      {/* On a phone an open conversation needs every pixel; the back row names it. */}
      <PageHeader
        title="Chats"
        description="Every conversation across your deals, in one place."
        className={cn("mb-6", selected && "hidden lg:flex")}
      />
      {content}
    </div>
  );
}
